import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Menu,
  Typography,
} from "@mui/material";
import NotificationsNoneRounded from "@mui/icons-material/NotificationsNoneRounded";
import { formatDistanceToNow } from "date-fns";
import {
  APP_TOP_BAR_ACTION_HIT_PX,
  APP_TOP_BAR_ACTION_ICON_PX,
} from "layout/appTopBarLayout";
import { useAuth } from "hooks/useAuth";
import {
  listUserNotifications,
  markNotificationRead,
} from "services/fde/notificationService";

const NotificationBell = () => {
  const { user } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!user?.uid) return;
    setLoading(true);
    try {
      const rows = await listUserNotifications({ uid: user.uid, limit: 25 });
      setNotifications(rows);
    } catch (err) {
      console.error("[NotificationBell] load failed", err);
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    load();
  }, [load]);

  const unreadCount = useMemo(
    () => notifications.filter((row) => row.read !== true).length,
    [notifications],
  );

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    load();
  };

  const handleClose = () => setAnchorEl(null);

  const handleMarkRead = async (row) => {
    if (row.read === true) return;
    try {
      await markNotificationRead({
        collection: row.collection,
        notificationId: row.id,
      });
      setNotifications((prev) =>
        prev.map((item) => (item.id === row.id ? { ...item, read: true } : item)),
      );
    } catch (err) {
      console.error("[NotificationBell] mark read failed", err);
    }
  };

  if (!user?.uid) return null;

  return (
    <>
      <IconButton
        onClick={handleOpen}
        aria-label="Notifications"
        sx={{
          width: APP_TOP_BAR_ACTION_HIT_PX,
          height: APP_TOP_BAR_ACTION_HIT_PX,
          flexShrink: 0,
          color: "primary.main",
        }}
      >
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsNoneRounded sx={{ fontSize: APP_TOP_BAR_ACTION_ICON_PX }} />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { width: 360, maxHeight: 420 } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2">Notifications</Typography>
        </Box>
        <Divider />
        {loading && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            Loading…
          </Typography>
        )}
        {!loading && !notifications.length && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No notifications yet.
          </Typography>
        )}
        <List dense disablePadding>
          {notifications.map((row) => (
            <ListItem
              key={`${row.collection}-${row.id}`}
              button
              onClick={() => handleMarkRead(row)}
              sx={{
                bgcolor: row.read ? "transparent" : "action.hover",
                alignItems: "flex-start",
              }}
            >
              <ListItemText
                primary={row.message || row.type || "Notification"}
                secondary={
                  row.createdAtDate
                    ? formatDistanceToNow(row.createdAtDate, { addSuffix: true })
                    : ""
                }
              />
            </ListItem>
          ))}
        </List>
      </Menu>
    </>
  );
};

export default NotificationBell;
